import {
  LayoutDashboard,
  Package,
  ClipboardList,
  PackagePlus,
  BarChart3,
  Settings as SettingsIcon,
  Tags,
  Megaphone,
  Truck,
  MessageSquareText,
  LayoutTemplate,
  CreditCard,
  Mail,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation, type TranslationKey } from '@dukanti/shared';

export interface NavItem {
  /** Route target. Fed to <Link to={...}>. */
  to: string;
  /** Translation key for the label. */
  labelKey: TranslationKey;
  /** Lucide icon component. */
  Icon: LucideIcon;
  /** Hide from staff because courier roster management is owner/admin-only. */
  requiresCourierManagement?: boolean;
  /** Hide from non-owners because billing creates financial obligations. */
  requiresBillingManagement?: boolean;
  /** Subscriber contact data is owner/admin-only. */
  requiresNewsletterSubscriberManagement?: boolean;
}

const BILLING_NAV_ITEM: NavItem = {
  to: '/billing',
  labelKey: 'admin.nav.billing',
  Icon: CreditCard,
  requiresBillingManagement: true,
};

/** Single source of truth for the admin sidebar's nav contract. */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'admin.nav.dashboard', Icon: LayoutDashboard },
  { to: '/products', labelKey: 'admin.nav.products', Icon: Package },
  { to: '/categories', labelKey: 'admin.nav.categories', Icon: Tags },
  { to: '/promotions', labelKey: 'admin.nav.promotions' as TranslationKey, Icon: Megaphone },
  { to: '/storefront', labelKey: 'admin.nav.storefront', Icon: LayoutTemplate },
  { to: '/orders', labelKey: 'admin.nav.orders', Icon: ClipboardList },
  {
    to: '/orders/prepare',
    labelKey: 'admin.nav.prepareOrder' as TranslationKey,
    Icon: PackagePlus,
  },
  { to: '/reviews', labelKey: 'admin.nav.reviews' as TranslationKey, Icon: MessageSquareText },
  {
    to: '/subscribers',
    labelKey: 'admin.nav.subscribers',
    Icon: Mail,
    requiresNewsletterSubscriberManagement: true,
  },
  {
    to: '/couriers',
    labelKey: 'admin.nav.couriers' as TranslationKey,
    Icon: Truck,
    requiresCourierManagement: true,
  },
  { to: '/stock', labelKey: 'admin.nav.stock', Icon: BarChart3 },
  { to: '/dashboard/settings', labelKey: 'admin.nav.settings', Icon: SettingsIcon },
];

/** Hydrated variant with translated labels. Consumers render from this. */
export interface HydratedNavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  contract: NavItem;
}

export interface NavItemVisibility {
  canManageCouriers?: boolean;
  canManageBilling?: boolean;
  canManageNewsletterSubscribers?: boolean;
}

export function filterNavItems(
  items: readonly NavItem[],
  visibility: NavItemVisibility = {},
): NavItem[] {
  const base = items.filter(
    (item) =>
      (!item.requiresCourierManagement || visibility.canManageCouriers === true) &&
      (!item.requiresNewsletterSubscriberManagement ||
        visibility.canManageNewsletterSubscribers === true),
  );
  if (visibility.canManageBilling !== true) return base;
  const promotionsIndex = base.findIndex((item) => item.to === '/promotions');
  if (promotionsIndex === -1) return [...base, BILLING_NAV_ITEM];
  return [
    ...base.slice(0, promotionsIndex + 1),
    BILLING_NAV_ITEM,
    ...base.slice(promotionsIndex + 1),
  ];
}

export function useNavItems(visibility: NavItemVisibility = {}): HydratedNavItem[] {
  const { t } = useTranslation();
  return filterNavItems(NAV_ITEMS, visibility).map((item) => ({
    to: item.to,
    label: t(item.labelKey),
    Icon: item.Icon,
    contract: item,
  }));
}

/** Pure fn so it's trivially unit-testable. */
export function isActive(item: NavItem, pathname: string): boolean {
  if (item.to === '/dashboard') return pathname === '/dashboard';
  if (item.to === '/dashboard/settings') {
    return pathname === '/dashboard/settings' || pathname.startsWith('/dashboard/settings/');
  }
  if (item.to === '/products') {
    return pathname === '/products' || pathname.startsWith('/products/');
  }
  if (item.to === '/categories') {
    return pathname === '/categories' || pathname.startsWith('/categories/');
  }
  if (item.to === '/promotions') {
    return pathname === '/promotions' || pathname.startsWith('/promotions/');
  }
  if (item.to === '/storefront') {
    return pathname.startsWith('/storefront');
  }
  if (item.to === '/billing') {
    return pathname === '/billing';
  }
  if (item.to === '/orders') {
    return (
      pathname === '/orders' ||
      (pathname.startsWith('/orders/') && !pathname.startsWith('/orders/prepare'))
    );
  }
  if (item.to === '/orders/prepare') {
    return pathname === '/orders/prepare';
  }
  if (item.to === '/reviews') {
    return pathname === '/reviews' || pathname.startsWith('/reviews/');
  }
  if (item.to === '/couriers') {
    return pathname === '/couriers' || pathname.startsWith('/couriers/');
  }
  if (item.to === '/subscribers') return pathname === '/subscribers';
  if (item.to === '/stock') {
    return pathname === '/stock' || pathname.startsWith('/stock/');
  }
  return false;
}

// F-027 §5.3 C2 — shadcn Sidebar does NOT auto-flip under dir="rtl"; it emits
// physical left-0/right-0 classes keyed on the `side` prop. We pass
// side={dirToSide(dir)} so RTL renders with side="right".
export function dirToSide(dir: 'ltr' | 'rtl'): 'left' | 'right' {
  return dir === 'rtl' ? 'right' : 'left';
}
